// Copyright © 2023 Move Closer

import { AnyObject, Eventbus, IEventbus } from '@movecloser/front-core'
import { Component, Prop, Vue } from 'vue-property-decorator'

import {
  PlayerState,
  UiVideoPlayerPauseEventPayload, UiVideoPlayerPlayEventPayload,
  UiVideoPlayerStateChangedEventPayload
} from '../../../_contracts'
import { YouTubeIframeLoader } from '../../../_helpers/youtube-iframe-loader'
import { resolveVideoIdFromUrl } from '../../Tile'

@Component<VideoPlayerYouTube>({
  name: 'VideoPlayerYouTube',

  mounted (): void {
    this.$nextTick(() => {
      if (this.shouldInit) {
        this.initPlayer()
      }
    })

    this.watchEvents()
  },

  beforeDestroy () {
    if (this.player && this.player.destroy) {
      this.player.destroy()
    }
    this.player = null
    this.clearEvents()
  },

  template: `
    <div>
      <component :is="'script'" type="application/ld+json" v-html="JSON.stringify(videoMetadata)" />

      <div :id="videoAttrId" :class="videoClass" ref="video" />
    </div>
  `
})
export class VideoPlayerYouTube extends Vue {
  @Prop({ type: Object, required: true })
  public readonly baseVideoMetadata!: AnyObject

  @Prop({ type: Object, required: false })
  public readonly eventBus?: IEventbus

  @Prop({ type: String, required: true })
  public readonly videoAttrId!: string

  @Prop({ type: String, required: false })
  public readonly videoAdsTag!: string

  @Prop({ type: String, required: true })
  public readonly videoSrc!: string

  @Prop({ type: String, required: false })
  public readonly videoClass?: string

  @Prop({ type: String, required: true })
  public readonly origin!: string

  @Prop({ type: Boolean, required: false, default: false })
  public readonly shouldInit!: boolean

  public isVideoPlaying: boolean = false

  private player: AnyObject | null = null
  private eventHandlers: (ReturnType<Eventbus['handle']>)[] = []

  public get videoMetadata (): AnyObject {
    return {
      ...this.baseVideoMetadata,
      embedUrl: `https://www.youtube.com/embed/${this.videoId}`
    }
  }

  public onPlay (event: UiVideoPlayerPlayEventPayload): void {
    if (typeof event.payload === 'undefined') {
      return
    }

    const { playerId, autoplay, muted } = event.payload

    if (playerId !== this.videoAttrId) {
      return
    }

    this.initPlayer(autoplay, muted)
  }

  public onPause (event: UiVideoPlayerPauseEventPayload): void {
    if (typeof event.payload === 'undefined') {
      return
    }

    const { playerId } = event.payload

    if (playerId !== this.videoAttrId) {
      return
    }

    if (this.player) {
      this.player.pauseVideo()
    }
  }

  public onStateChanged (event: UiVideoPlayerStateChangedEventPayload): void {
    if (this.player === null || typeof event.payload === 'undefined') {
      return
    }

    const { playerId, state } = event.payload

    if (playerId === this.videoAttrId) {
      return
    }

    if (state === PlayerState.Playing) {
      this.player.pauseVideo()
    }
  }

  private watchEvents () {
    if (!this.eventBus) {
      return
    }

    this.eventHandlers = [
      this.eventBus.handle('ui:video-player.play', this.onPlay),
      this.eventBus.handle('ui:video-player.pause', this.onPause),
      this.eventBus.handle('ui:video-player.state-changed', this.onStateChanged)
    ]
  }

  private clearEvents () {
    this.eventHandlers.forEach(handler => handler.unsubscribe())
  }

  /**
   * Initialises the YouTube player.
   *
   * @param autoplay - Determines whether the video should start playing
   *   immediately after the player has been initialised.
   * @param muted - Determine whether the video should be started with volume 0.
   */
  private initPlayer (autoplay: boolean = false, muted: boolean = false): void {
    this.$emit('init')

    // @ts-expect-error
    YouTubeIframeLoader.load(YT => {
      this.player = new YT.Player(this.videoAttrId, {
        events: {
          onReady: ({ target }: { target: AnyObject }) => {
            this.player = target

            if (!autoplay || !this.player) {
              return
            }

            if (muted) {
              target.mute()
            }

            // prevent "Failed to execute 'postMessage' on 'DOMWindow'" error
            // https://stackoverflow.com/a/56607403/3305944
            setTimeout(() => target.playVideo(), 500)
          },

          onStateChange: (event: { target: AnyObject; data: PlayerState }) => {
            const { data: state } = event

            if (typeof state !== 'number') {
              console.warn(`YT.Player.onStateChange(): Expected [event.data] to be a type of [number], but got [${typeof state}]!`)
              return
            }

            if (this.player === null) {
              console.warn('YT.Player.onStateChange(): FATAL! [this.player] is [null]!')
              return
            }

            this.isVideoPlaying = state === PlayerState.Playing

            const payload: UiVideoPlayerStateChangedEventPayload['payload'] = {
              playerId: this.videoAttrId,
              state
            }

            if (this.eventBus) {
              this.eventBus.emit('ui:video-player.state-changed', payload)
            }

            if (this.isVideoPlaying) {
              this.$emit('play')
            } else {
              this.$emit('pause')
            }
          }
        },
        height: '100%',
        width: '100%',
        videoId: this.videoId,
        enablejsapi: 1,
        playsinline: 0,
        origin: this.origin
      })
    })
  }

  private get videoId (): string | undefined {
    return resolveVideoIdFromUrl(this.videoSrc)
  }
}

export default VideoPlayerYouTube
